import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnalysisAPI, OdkAPI } from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import { Card, PageHeader, Spinner, ErrorBanner, InfoBanner, HelpBox, KindBadge, AccionesSeleccion } from "../components/Layout";
import PlotlyChart from "../components/Plot";
import { varKind } from "../lib/varkind";
import type { Data } from "plotly.js-dist-min";

/** Una fila del diagnóstico: todo lo que hay que saber de un coeficiente. */
interface Termino {
  variable: string;
  es_constante: boolean;
  coef: number | null;
  p: number | null;
  ic_bajo: number | null;
  ic_alto: number | null;
  vif: number | null;
  /** exp(coef): odds ratio en Logit, razón de tasas en Poisson. */
  razon?: number | null;
  razon_bajo?: number | null;
  razon_alto?: number | null;
  /** Efecto marginal medio: puntos porcentuales en Logit/Probit. */
  efecto?: number | null;
  efecto_unidad?: string;
  efecto_p?: number | null;
  /** "de 0 a 1" para una dummy; "una unidad más" para una continua. */
  salto?: string;
  prob_desde?: number | null;
  prob_hasta?: number | null;
}

interface RegressionResult {
  error?: string;
  modelo?: string;
  r_squared?: number | null;
  coefficients?: Record<string, number>;
  conf_int?: Record<string, [number, number] | Record<string, number>>;
  p_values?: Record<string, number>;
  vif?: Record<string, number | null>;
  fitted_values?: number[];
  residuals?: number[];
  n_obs?: number;
  summary?: string;
  terminos?: Termino[];
  /** "odds" (Logit), "tasa" (Poisson) o "ninguna" (OLS/RLM/Probit). */
  escala_efecto?: "odds" | "tasa" | "ninguna";
  probabilidades?: {
    observada: number | null;
    media_predicha: number | null;
    caso_medio: number | null;
  } | null;
  /** Cómo entró cada X: numérica, o categórica con cuántas dummies. */
  tratamiento?: Record<string, string>;
  /** Lo que se hizo sin preguntar: columnas descartadas, ajuste que no convergió. */
  avisos?: string[];
}

// Documentación en pantalla de cada tipo de modelo.
const MODEL_DOCS: Record<string, { name: string; uso: string; requisitoY: string; ejemplo: string }> = {
  OLS: {
    name: "OLS — Mínimos Cuadrados Ordinarios",
    uso: "Regresión lineal clásica: estima cuánto cambia Y (continua) por cada unidad de cambio en cada X.",
    requisitoY: "Y debe ser numérica continua (edad, ingreso, puntaje...).",
    ejemplo: "Ej.: ¿cómo influyen edad y educación sobre el ingreso?",
  },
  Logit: {
    name: "Logit — Regresión logística",
    uso: "Modela la probabilidad de un evento binario (sí/no). Los coeficientes son log-odds: positivo = aumenta la probabilidad.",
    requisitoY: "Y debe ser binaria (0/1). Si tu variable es categórica, crea aquí mismo la dummy eligiendo la categoría que vale 1.",
    ejemplo: "Ej.: ¿qué factores predicen que alguien vote 'Sí'?",
  },
  Probit: {
    name: "Probit",
    uso: "Igual que Logit (evento binario) pero usa la distribución normal acumulada. Resultados casi siempre equivalentes a Logit.",
    requisitoY: "Y debe ser binaria (0/1). Usa la dummy si tu variable es categórica.",
    ejemplo: "Ej.: probabilidad de aprobar/desaprobar una gestión.",
  },
  Poisson: {
    name: "Poisson — Conteos",
    uso: "Para variables de conteo: número de eventos por unidad (visitas, hijos, quejas). Modela la tasa esperada.",
    requisitoY: "Y debe ser un conteo entero no negativo (0, 1, 2, ...).",
    ejemplo: "Ej.: ¿qué explica el número de atenciones recibidas?",
  },
  RLM: {
    name: "RLM — Regresión robusta",
    uso: "Como OLS pero resistente a valores atípicos (outliers): les da menos peso en vez de dejar que distorsionen el modelo.",
    requisitoY: "Y numérica continua. Úsala si sospechas outliers fuertes.",
    ejemplo: "Ej.: ingreso con algunos valores extremos que no quieres eliminar.",
  },
};

export default function RegressionPage() {
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [yCol, setYCol] = useState<string>("");
  const [xCols, setXCols] = useState<string[]>([]);
  const [modelType, setModelType] = useState("OLS");
  const [standardize, setStandardize] = useState(false);
  const [dummyVal, setDummyVal] = useState<string>("");
  const [result, setResult] = useState<RegressionResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const colsQ = useQuery({
    queryKey: ["dict", projectId, formId],
    queryFn: () => OdkAPI.variables(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });
  const cols = (colsQ.data ?? []).filter((v) => !v.is_technical);
  const yEntry = cols.find((c) => c.name === yCol);
  const yIsCategorical = yEntry ? varKind(yEntry) !== "numeric" : false;

  // Categorías reales de Y en los datos (para construir la dummy 0/1).
  const yCatsQ = useQuery({
    queryKey: ["freq", projectId, formId, yCol],
    queryFn: async () => {
      const r = await AnalysisAPI.descriptives({
        project_id: projectId!, form_id: formId!, variables: [yCol],
      });
      const entry = (r as Record<string, { counts?: Record<string, number> }>)[yCol];
      return Object.keys(entry?.counts ?? {});
    },
    enabled: !!projectId && !!formId && !!yCol && yIsCategorical,
  });
  const yCategories = yCatsQ.data ?? [];

  const toggleX = (c: string) =>
    setXCols((xs) => (xs.includes(c) ? xs.filter((x) => x !== c) : [...xs, c]));

  const selectableX = cols.filter((c) => c.name !== yCol);

  const needsDummy = yIsCategorical;
  const canRun = !!yCol && xCols.length > 0 && (!needsDummy || !!dummyVal);

  const run = async () => {
    if (!projectId || !formId || !canRun) return;
    setBusy(true); setErr(null);
    try {
      const r = await AnalysisAPI.regression({
        project_id: projectId, form_id: formId, y_col: yCol,
        x_cols: xCols.filter((x) => x !== yCol),
        model_type: modelType, standardize_x: standardize,
        dummy_var: needsDummy && dummyVal ? yCol : undefined,
        dummy_val: needsDummy && dummyVal ? dummyVal : undefined,
      });
      setResult(r as RegressionResult);
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
    finally { setBusy(false); }
  };

  const doc = MODEL_DOCS[modelType];

  // Una fila por coeficiente, sin la constante (que no se interpreta).
  const terminos = (result?.terminos ?? []).filter((t) => !t.es_constante);
  const escala = result?.escala_efecto ?? "ninguna";
  // Odds ratio en Logit, razón de tasas en Poisson. En OLS, Probit y RLM exp(coef)
  // no es ninguna de las dos cosas: la columna no aparece en vez de enseñar un
  // número que se leería como odds ratio.
  const tituloRazon = escala === "odds" ? "Odds ratio (IC 95%)" : "Razón de tasas (IC 95%)";
  const hayEfecto = terminos.some((t) => t.efecto != null);
  const hayProbabilidad = terminos.some((t) => t.prob_hasta != null);
  const pct = (v: number | null | undefined) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

  const coefTrace: Data = {
    type: "bar",
    x: terminos.map((t) => t.variable),
    y: terminos.map((t) => t.coef),
    marker: { color: terminos.map((t) => ((t.coef ?? 0) >= 0 ? "#0ea5e9" : "#f87171")) },
    error_y: {
      type: "data",
      symmetric: false,
      array: terminos.map((t) => (t.ic_alto ?? t.coef ?? 0) - (t.coef ?? 0)),
      arrayminus: terminos.map((t) => (t.coef ?? 0) - (t.ic_bajo ?? t.coef ?? 0)),
    },
  };

  // En Logit y Probit los ajustados son PROBABILIDADES, no log-odds: el backend
  // devuelve `predict()`, que es la escala en la que la gente lee el gráfico.
  const esProbabilidad = modelType === "Logit" || modelType === "Probit";
  const fittedTrace: Data = {
    type: "scatter",
    mode: "lines",
    name: esProbabilidad ? "Probabilidad estimada" : "Ajustado",
    x: (result?.fitted_values ?? []).map((_, i) => i + 1),
    y: result?.fitted_values ?? [],
    line: { color: "#0ea5e9" },
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader title="Regresión" subtitle="OLS / Logit / Probit / Poisson / RLM con diagnóstico." />

      <HelpBox title="Guía: tipos de regresión, dummies y diagnóstico">
        <p><b>¿Qué hace esta página?</b> Estima cómo una o más variables independientes (X) explican una variable dependiente (Y).</p>
        <div>
          <b>Tipos de modelo disponibles:</b>
          <ul className="list-disc ml-5 mt-1 space-y-1">
            {Object.values(MODEL_DOCS).map((d) => (
              <li key={d.name}><b>{d.name}.</b> {d.uso} <i>{d.requisitoY}</i> {d.ejemplo}</li>
            ))}
          </ul>
        </div>
        <p>
          <b>Variables dummy.</b> Una dummy convierte una categoría en 0/1 (ej.: voto = "Sí" → 1, resto → 0).
          {" "}Si eliges una <b>Y categórica</b>, aparecerá un selector para crear la dummy aquí mismo (no necesitas
          transformar los datos fuera). Las <b>X categóricas</b> no requieren nada: el sistema las convierte
          automáticamente en dummies (una por categoría, omitiendo la primera como referencia).
        </p>
        <p>
          <b>Estandarizar X (Z-score).</b> Reescala cada X a media 0 y desviación 1; útil para comparar la
          importancia relativa de predictores medidos en unidades distintas.
        </p>
        <p>
          <b>Cómo leer los resultados.</b> <b>Coef</b>: efecto de esa X sobre Y (con su intervalo de confianza al 95%).
          {" "}<b>p</b>: significancia (p &lt; 0.05 = efecto estadísticamente significativo).
          {" "}<b>VIF</b>: multicolinealidad (VIF &gt; 10 indica que esa X está muy correlacionada con otras X y su
          coeficiente es poco fiable). <b>R²</b>: porcentaje de la variación de Y que el modelo explica.
        </p>
        <p>
          <b>Odds ratio y probabilidades (Logit y Probit).</b> El coeficiente de una logística está en
          log-odds, que no se lee. Por eso la tabla añade el <b>odds ratio</b> —exp(coef): 1 = no cambia
          nada, y si su intervalo cruza el 1 el efecto no está demostrado—, el <b>efecto en puntos
          porcentuales</b> (cuánto mueve la probabilidad, en promedio) y <b>de qué probabilidad a qué
          probabilidad</b> pasa el caso medio. En Poisson, exp(coef) es una <b>razón de tasas</b>. En OLS
          y RLM no hay odds ratio y la columna no aparece, en vez de enseñar un número que se leería mal.
        </p>
        <p>
          <b>Si el modelo no se puede estimar</b> («Singular matrix»), suele ser porque dos X dicen lo
          mismo, o porque una X categórica tiene demasiadas categorías y genera más columnas que casos.
          El sistema lo detecta, descarta la columna repetida y <b>avisa arriba de cuál fue</b>; los
          topes se ajustan en <b>Configuración → Análisis</b>.
        </p>
      </HelpBox>

      <Card className="mb-4">
        <ProjectFormPicker projectId={projectId} formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); }} onForm={setFormId} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="etiqueta">Variable dependiente (Y)</label>
            <select className="input" value={yCol}
              onChange={(e) => { setYCol(e.target.value); setDummyVal(""); }}>
              <option value="">—</option>
              {cols.map((c) => (
                <option key={c.name} value={c.name}>
                  {(c.label || c.name) + (varKind(c) === "numeric" ? "" : " (categórica)")}
                </option>
              ))}
            </select>
            {yEntry && (
              <div className="mt-1 flex items-center gap-2 text-xs text-tinta-3">
                <KindBadge kind={varKind(yEntry)} />
                {!yIsCategorical && <span>Lista para usar como Y numérica.</span>}
              </div>
            )}
          </div>
          <div>
            <label className="etiqueta">Modelo</label>
            <select className="input" value={modelType} onChange={(e) => setModelType(e.target.value)}>
              <option>OLS</option><option>Logit</option><option>Probit</option><option>Poisson</option><option>RLM</option>
            </select>
            {doc && (
              <p className="mt-1 text-xs text-tinta-3">
                <b className="text-tinta-2">{doc.name}.</b> {doc.uso} <span className="text-estado-serio">{doc.requisitoY}</span>
              </p>
            )}
          </div>
        </div>

        {yIsCategorical && yCol && (
          <div className="mt-3 p-3 rounded-lg bg-estado-warning/10 border border-estado-warning/40">
            <div className="text-sm text-estado-serio font-medium mb-1">Crear variable dummy para Y</div>
            <p className="text-xs text-tinta-2 mb-2">
              "{yEntry?.label || yCol}" no es numérica. Elige la categoría que valdrá <b>1</b>;
              todas las demás valdrán <b>0</b>. Con esa dummy puedes usar Logit o Probit.
            </p>
            <select className="input sm:w-80"
              value={dummyVal} onChange={(e) => setDummyVal(e.target.value)}>
              <option value="">— elegir la categoría que vale 1 —</option>
              {yCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {yCatsQ.isLoading && <div className="mt-1 text-xs text-tinta-3">Cargando categorías reales de los datos…</div>}
          </div>
        )}

        <div className="mt-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <label className="etiqueta mb-0">Independientes (X)</label>
            <AccionesSeleccion
              visibles={selectableX.map((c) => c.name)}
              seleccionados={xCols}
              onCambiar={setXCols}
              nombre="variable(s)"
            />
          </div>
          <div className="max-h-40 overflow-y-auto bg-white border border-grid rounded-md p-2">
            {selectableX.map((c) => (
              <label key={c.name} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                <input type="checkbox" checked={xCols.includes(c.name)} onChange={() => toggleX(c.name)} className="accent-marca" />
                <span>{c.label || c.name}</span>
                <KindBadge kind={varKind(c)} />
                {varKind(c) === "categorical" && (
                  <span className="text-[10px] text-tinta-3">→ dummies automáticas</span>
                )}
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm">
          <input type="checkbox" checked={standardize} onChange={(e) => setStandardize(e.target.checked)} className="accent-marca" />
          Estandarizar X (Z-score)
        </label>
        <button onClick={run} disabled={busy || !canRun}
          className="btn mt-3">
          {busy ? <Spinner /> : "Calcular modelo"}
        </button>
        {needsDummy && !dummyVal && yCol && (
          <span className="ml-3 text-xs text-estado-serio">Elige la categoría de la dummy para poder calcular.</span>
        )}
      </Card>

      {err && <ErrorBanner message={err} />}
      {result?.error && <ErrorBanner message={result.error} />}

      {/* Lo que el modelo hizo sin preguntar. Una columna que desaparece en silencio
          cambia el modelo que el usuario cree estar leyendo. */}
      {(result?.avisos ?? []).map((a, i) => (
        <div key={i} className="mb-3"><InfoBanner message={a} /></div>
      ))}

      {result && !result.error && (
        <div className="space-y-4">
          <Card>
            <div className="text-sm text-tinta-2">
              R² = {result.r_squared != null ? (result.r_squared * 100).toFixed(2) + "%" : "—"} · n = {result.n_obs}
              {needsDummy && dummyVal && (
                <span className="text-tinta-3"> · Y = 1 cuando "{yEntry?.label || yCol}" = "{dummyVal}"</span>
              )}
            </div>
            {result.tratamiento && Object.keys(result.tratamiento).length > 0 && (
              <div className="mt-1 text-xs text-tinta-3">
                Cómo entró cada variable:{" "}
                {Object.entries(result.tratamiento).map(([v, como], i) => (
                  <span key={v}>{i > 0 && " · "}<b className="text-tinta-2">{v}</b>: {como}</span>
                ))}
              </div>
            )}
          </Card>

          {result.probabilidades && (
            <Card>
              <div className="text-sm font-medium text-tinta-2 mb-2">Probabilidades</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xl font-semibold text-marca">{pct(result.probabilidades.observada)}</div>
                  <div className="text-xs text-tinta-3">Observada en los datos: qué proporción de casos tiene Y = 1.</div>
                </div>
                <div>
                  <div className="text-xl font-semibold text-marca">{pct(result.probabilidades.media_predicha)}</div>
                  <div className="text-xs text-tinta-3">Media de las probabilidades que estima el modelo. Debería parecerse a la observada.</div>
                </div>
                <div>
                  <div className="text-xl font-semibold text-marca">{pct(result.probabilidades.caso_medio)}</div>
                  <div className="text-xs text-tinta-3">Caso medio: probabilidad con TODAS las X en su promedio. Es el punto de partida de la columna «De → a».</div>
                </div>
              </div>
            </Card>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <div className="text-sm font-medium text-tinta-2 mb-2">Coeficientes (IC 95%)</div>
              <div className="h-72"><PlotlyChart data={[coefTrace]} layout={{ xaxis: { tickangle: -25 } }} /></div>
            </Card>
            <Card>
              <div className="text-sm font-medium text-tinta-2 mb-2">
                {esProbabilidad ? "Probabilidad estimada por caso" : "Valores ajustados"}
              </div>
              <div className="h-72">
                <PlotlyChart
                  data={[fittedTrace]}
                  layout={{
                    xaxis: { title: "Observación" },
                    yaxis: esProbabilidad
                      ? { title: "Probabilidad", tickformat: ".0%", range: [0, 1] }
                      : { title: "Ajustado" },
                  }}
                />
              </div>
              {esProbabilidad && (
                <p className="mt-2 text-xs text-tinta-3">
                  Probabilidad, no log-odds: hasta la v3.3.0 aquí se dibujaba la escala interna
                  del modelo, que va de −∞ a +∞ y no se puede leer.
                </p>
              )}
            </Card>
          </div>
          <Card>
            <div className="text-sm font-medium text-tinta-2 mb-2">Diagnóstico</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-tinta-3 border-b border-grid">
                  <tr>
                    <th className="py-2 pr-3 text-left">Variable</th>
                    <th className="text-right pr-3">Coef</th>
                    {escala !== "ninguna" && (
                      <th className="text-right pr-3 whitespace-nowrap" title="exp(coeficiente) con su intervalo de confianza al 95%">
                        {tituloRazon}
                      </th>
                    )}
                    {hayEfecto && (
                      <th className="text-right pr-3 whitespace-nowrap" title="Efecto marginal medio: cuánto mueve esta variable el resultado, en promedio, en toda la muestra">
                        Efecto
                      </th>
                    )}
                    {hayProbabilidad && (
                      <th className="text-right pr-3 whitespace-nowrap" title="Probabilidad antes y después del cambio, con las demás variables en su promedio">
                        Probabilidad: de → a
                      </th>
                    )}
                    <th className="text-right pr-3">p</th>
                    <th className="text-right pr-3">VIF</th>
                  </tr>
                </thead>
                <tbody>
                  {terminos.map((t) => (
                    <tr key={t.variable} className="border-b border-grid">
                      <td className="py-2 pr-3 font-mono text-marca">{t.variable}</td>
                      <td className="text-right pr-3">{t.coef?.toFixed(4) ?? "—"}</td>
                      {escala !== "ninguna" && (
                        <td className="text-right pr-3 whitespace-nowrap">
                          {t.razon != null ? (
                            <>
                              <b>{t.razon.toFixed(3)}</b>
                              <span className="text-[10px] text-tinta-3">
                                {" "}[{t.razon_bajo?.toFixed(2) ?? "—"}–{t.razon_alto?.toFixed(2) ?? "—"}]
                              </span>
                            </>
                          ) : "—"}
                        </td>
                      )}
                      {hayEfecto && (
                        <td className="text-right pr-3 whitespace-nowrap">
                          {t.efecto != null
                            ? `${t.efecto > 0 ? "+" : ""}${t.efecto.toFixed(2)}${t.efecto_unidad === "pp" ? " pp" : ""}`
                            : "—"}
                        </td>
                      )}
                      {hayProbabilidad && (
                        <td className="text-right pr-3 whitespace-nowrap">
                          {t.prob_hasta != null ? (
                            <>
                              {pct(t.prob_desde)} → {pct(t.prob_hasta)}
                              <span className="block text-[10px] text-tinta-3">{t.salto}</span>
                            </>
                          ) : "—"}
                        </td>
                      )}
                      <td className={`text-right pr-3 ${t.p != null && t.p < 0.05 ? "font-medium text-tinta" : ""}`}>
                        {t.p?.toFixed(4) ?? "—"}
                      </td>
                      <td className={`text-right pr-3 ${(t.vif ?? 0) > 10 ? "text-estado-critico" : ""}`}>
                        {t.vif != null ? t.vif.toFixed(2) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-xs text-tinta-3 space-y-1">
              <p>
                <b>p</b> &lt; 0.05: efecto significativo · <b>VIF</b> &gt; 10: esa X está muy
                correlacionada con otras y su coeficiente es poco fiable.
              </p>
              {escala === "odds" && (
                <p>
                  <b>Odds ratio.</b> Multiplica los <i>momios</i> (la razón entre que pase y que no pase),
                  no la probabilidad: 1 = no cambia nada, 2 = los duplica, 0,5 = los reduce a la mitad.
                  Si el intervalo entre corchetes <b>cruza el 1</b>, el efecto no está demostrado.
                </p>
              )}
              {escala === "tasa" && (
                <p>
                  <b>Razón de tasas.</b> Por cuánto se multiplica el número esperado de eventos:
                  1,20 = un 20% más. Si el intervalo cruza el 1, el efecto no está demostrado.
                </p>
              )}
              {hayEfecto && esProbabilidad && (
                <p>
                  <b>Efecto</b> en puntos porcentuales (pp): cuánto sube o baja la probabilidad, en
                  promedio en toda la muestra, cuando esa X aumenta una unidad (o pasa de 0 a 1 si es
                  una dummy). Es la lectura directa; el odds ratio es la relativa.
                </p>
              )}
              {hayProbabilidad && (
                <p>
                  <b>De → a</b>: probabilidad antes y después de ese cambio, dejando las demás
                  variables en su promedio. Sirve para contarlo en una frase: «pasa del 42% al 61%».
                </p>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
